import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";

const CYRILLIC_CONFUSABLES = {
    "\u0430": "a",
    "\u0410": "a",
    "\u0435": "e",
    "\u0415": "e",
    "\u043e": "o",
    "\u041e": "o",
    "\u0440": "p",
    "\u0420": "p",
    "\u0441": "c",
    "\u0421": "c",
    "\u0445": "x",
    "\u0425": "x",
    "\u0443": "y",
    "\u0423": "y",
    "\u043a": "k",
    "\u041a": "k",
    "\u043c": "m",
    "\u041c": "m",
    "\u0442": "t",
    "\u0422": "t",
    "\u0432": "b",
    "\u0412": "b",
    "\u043d": "h",
    "\u041d": "h",
    "\u0456": "i",
    "\u0406": "i",
    "\u0457": "i",
    "\u0407": "i",
    "\u0451": "e",
    "\u0401": "e",
};

export const normalizeReviewerSafetyName = (value = "") => {
    const mapped = String(value || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .split("")
        .map((char) => CYRILLIC_CONFUSABLES[char] || char)
        .join("")
        .toLowerCase();

    return mapped.replace(/[^a-z0-9]/g, "");
};

export const findReviewerPenNameConflict = (reviewerName, authors = []) => {
    const reviewerPattern = normalizeReviewerSafetyName(reviewerName);
    if (!reviewerPattern || reviewerPattern.length < 3) return null;

    const authorList = Array.isArray(authors) ? authors : [authors];

    for (const entry of authorList) {
        const authorName = typeof entry === "string" ? entry : entry?.author;
        const authorPattern = normalizeReviewerSafetyName(authorName);
        if (!authorPattern || authorPattern.length < 3) continue;

        const canComparePartial = Math.min(reviewerPattern.length, authorPattern.length) >= 3;
        const isConflict =
            reviewerPattern === authorPattern ||
            (canComparePartial &&
                (reviewerPattern.includes(authorPattern) || authorPattern.includes(reviewerPattern)));

        if (isConflict) {
            return {
                reviewerName: reviewerName || "",
                authorName: authorName || "",
                reviewerPattern,
                authorPattern,
            };
        }
    }

    return null;
};

const displayName = (value) => {
    const trimmed = String(value || "").trim();
    return trimmed || "not set";
};

export const ReviewerNameSafetyModal = ({ show, conflict, onCancel, onContinue }) => {
    const [acknowledged, setAcknowledged] = useState(false);

    useEffect(() => {
        if (show) setAcknowledged(false);
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={onCancel}
            backdrop="static"
            keyboard={false}
            size="lg"
            dialogClassName="mt-3"
        >
            <Modal.Header>
                <Modal.Title className="text-danger fs-6">Amazon Reviewer Name Safety Warning</Modal.Title>
            </Modal.Header>
            <Modal.Body className="small">
                <Alert variant="danger" className="mb-3 py-2">
                    <div className="fw-bold mb-2">
                        Potential Account Safety Warning
                    </div>

                    <div className="mb-2">
                        We detected a potentially risky name pattern between your reviewer identity and an author or pen name associated with your account. While this does not automatically mean there is a problem, we want to make sure you are aware of a
                        potential risk before continuing.
                    </div>

                    <div>
                        Your reviewer name <strong>{displayName(conflict?.reviewerName)}</strong> looks very similar to your pen name <strong>{displayName(conflict?.authorName)}</strong>.
                    </div>
                </Alert>

                <p className="mb-2">
                    Amazon's review moderation systems are not always transparent or predictable. Over the years, we have seen
                    situations where accounts were manually or automatically flagged because reviewer identities appeared to be
                    connected to the author. Even when no rules were intentionally broken, resolving these cases often required
                    lengthy communication with Amazon support.
                </p>

                <p className="mb-2">
                    To help reduce unnecessary risk to your Amazon account, we strongly recommend following these best practices:
                </p>

                <ol className="mb-2">
                    <li>
                        <strong>Never post reviews from the same Amazon account that you use to manage your KDP publishing account.</strong>
                        Keeping publishing and reviewing activities separate is one of the safest approaches.
                    </li>

                    <li>
                        <strong>Avoid using reviewer names that match or closely resemble your author name or any of your pen names.</strong>
                        Even partial similarities may create unnecessary attention during automated checks.
                    </li>

                    <li>
                        <strong>If possible, use a different computer and a different internet connection (IP address)</strong> when
                        writing reviews than the one normally used to access your KDP dashboard.
                    </li>
                </ol>

                <p className="mb-2">
                    These recommendations are based on real situations reported by authors using our platform. They cannot guarantee
                    that Amazon will never review your account, but they can significantly reduce the likelihood of avoidable account
                    verification requests or unnecessary support conversations.
                </p>

                <p className="mb-3">
                    Our goal is not to alarm you. We simply want to help you protect your publishing account by making sure you are
                    aware of a pattern that deserves extra attention before you continue.
                </p>
                <Alert variant="warning" className="mb-1 py-1">

                <Form.Check
                    type="checkbox"
                    id="reviewer-name-safety-ack"
                    checked={acknowledged}
                    className="custom-checkbox ms-0 mt-1 mb-1"
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    label={
                        <span className="fw-semibold">
            I have read this warning and understand the risk.
        </span>
                    }
                />
                </Alert>
            </Modal.Body>
            <Modal.Footer className="py-2">
                <Button variant="outline-secondary" size="sm" onClick={onCancel}>
                    Go back and edit
                </Button>
                <Button variant="danger" size="sm" onClick={onContinue} disabled={!acknowledged}>
                    I understand, continue
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
